import { Incident } from "../models/Incident";
import { map, uniqBy, forEach, reduce, filter, some } from 'lodash';
import { SEVERITY, IDLE_TIME_INCIDENT_NAME, HOS_INCIDENT_CATEGORY, OVERALL_HOS_INCIDENT_NAME } from "../constants";

export interface IIncidentDetails {
	name?: string;
	events?: number;
	details?: string;
	severity?: SEVERITY;
}

// The scorecard emits placeholder records for violations it counted but whose detail
// records could not be found.  These always carry an incidentID of -1 (real incidents
// are always >= 1).  They are still counted as events on the scorecard; on the incident
// detail page their occurrence row shows a "details missing" message in place of the
// (absent) date.
const PLACEHOLDER_INCIDENT_ID = -1;

export function isPlaceholderIncident(incident?: Incident): boolean {
	return incident?.incidentID === PLACEHOLDER_INCIDENT_ID;
}

// All HOS-category incidents (ticket 13004) are rolled into a single "Overall HOS"
// scorecard entry regardless of their individual violation name; the per-violation
// breakdown is shown on the incident detail page instead.
export function isHosIncident(incident?: Incident): boolean {
	return incident?.incidentCategory === HOS_INCIDENT_CATEGORY;
}

// Only CRITICAL/WARNING incidents are ever surfaced as a visible scorecard entry;
// shared so buildIncidents (below) and the manager driver carousel (managerHelper.tsx)
// can't drift on what counts as "visible enough to show a panel for".
export function isVisibleSeverity(severity?: SEVERITY): boolean {
	return severity === SEVERITY.CRITICAL || severity === SEVERITY.WARNING;
}

// True when at least one HOS-category incident in the list is severe enough that
// buildIncidents would actually surface an "Overall HOS" rollup panel for it.
export function hasVisibleHosIncident(incidents?: Incident[]): boolean {
	return some(incidents, inc => isHosIncident(inc) && isVisibleSeverity(inc.severity));
}

function groupKey(incident: Incident): string | undefined {
	return isHosIncident(incident) ? OVERALL_HOS_INCIDENT_NAME : incident.name;
}

// Worst severity across a group of HOS incidents: CRITICAL beats WARNING beats anything else.
function worstSeverity(incidents: Incident[]): SEVERITY | undefined {
	if (some(incidents, inc => inc.severity === SEVERITY.CRITICAL)) return SEVERITY.CRITICAL;
	if (some(incidents, inc => inc.severity === SEVERITY.WARNING)) return SEVERITY.WARNING;
	return incidents[0]?.severity;
}

export function buildIncidents(incidents:Incident[]|undefined) : [IIncidentDetails] {
	if (incidents) {

		//Currently we are using name since Incident doesn't seem to have type or something - Arthur
		const unique  = filter(map(uniqBy(incidents, groupKey), inc => {
			const severity = isHosIncident(inc) ? worstSeverity(filter(incidents, isHosIncident)) : inc.severity;
			return {name: groupKey(inc), severity, details: inc.details}
		}), inc => isVisibleSeverity(inc.severity));

		forEach(unique, un => {
			un.events = reduce( filter(incidents, inc => groupKey(inc) === un.name), (sum, inc) => sum + 1, 0 )
		})

		forEach(unique, un => {
			if (un.name !== IDLE_TIME_INCIDENT_NAME) {
				un.details = `${un?.events} events`
			}
		})
		return unique
	}
	else return [] as any as [IIncidentDetails]
}